import React, { useEffect, useMemo, useState } from 'react';
import { useStore, uid } from '../state/store';
import { Card, Chip, Banner, Money } from './ui';
import { qualificationCandidates } from '../lib/funnel';
import { deriveOperatingMode } from '../lib/operatingMode';
import { applySetupWorkPackageSync } from '../lib/setupCompletion';
import { orgTimeTargets } from '../lib/orgPlan';

/** Finish Setup — dominant while operatingMode === setup; disappears when ready. */
export function FinishSetup({ go }) {
  const { state, d, set, patch, add, today } = useStore();
  const operating = deriveOperatingMode(state, d);
  const timeTargets = orgTimeTargets(state);
  const accounts = state.accounts || [];
  const recommended = useMemo(() => {
    const fromQualify = qualificationCandidates(accounts, { cap: 15 });
    const priorityExisting = accounts
      .filter((a) => a.tier === 'Tier A' || a.tier === 'A')
      .map((a) => ({
        id: a.id,
        name: a.name,
        proposedTier: 'Priority',
        reason: 'Already on your priority list',
        score: 99,
        already: true,
        recommended: true,
      }));
    const ids = new Set(priorityExisting.map((r) => r.id));
    const merged = [...priorityExisting, ...fromQualify.tierA.filter((r) => !ids.has(r.id)).map((r) => ({
      ...r,
      proposedTier: 'Recommended',
      recommended: true,
    }))].slice(0, 15);
    if (merged.length < 10) {
      for (const r of fromQualify.qualifiedTargets) {
        if (merged.length >= 15) break;
        if (merged.some((m) => m.id === r.id)) continue;
        merged.push({ ...r, proposedTier: 'Alternate', recommended: false });
      }
    }
    if (merged.length < 8) {
      for (const a of accounts) {
        if (merged.length >= 12) break;
        if (merged.some((m) => m.id === a.id) || a.tier === 'Parked') continue;
        merged.push({
          id: a.id,
          name: a.name,
          proposedTier: 'Alternate',
          reason: a.classificationWhy || 'Imported account — review fit',
          score: Number(a.crmAccountScore) || 0,
          recommended: false,
        });
      }
    }
    return merged.slice(0, 15);
  }, [accounts]);

  const recommendedRows = recommended.filter((r) => r.recommended !== false && (r.proposedTier === 'Recommended' || r.proposedTier === 'Priority' || r.already));
  const alternateRows = recommended.filter((r) => !recommendedRows.some((x) => x.id === r.id));

  const [selected, setSelected] = useState(() => new Set());
  const [msg, setMsg] = useState('');
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || !recommended.length) return;
    const seedIds = recommended
      .filter((r) => r.recommended !== false)
      .slice(0, Math.min(12, recommended.length))
      .map((r) => r.id);
    setSelected(new Set(seedIds.length ? seedIds : recommended.slice(0, 12).map((r) => r.id)));
    setSeeded(true);
  }, [recommended, seeded]);

  // When cash/burn become valid, complete runway WP (not on Unknown-proceed alone).
  // store.patch upserts on 404 so client-migrated seed WPs missing on the API do not banner.
  useEffect(() => {
    if (!operating.runwayOk && !state.ui?.runwayUnknownProceed) return;
    applySetupWorkPackageSync(state, d, { today, patch });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional on runway/queue readiness edges
  }, [operating.runwayOk, state.ui?.runwayUnknownProceed, state.runway?.cashToday, state.runway?.monthlyBurn]);

  if (operating.mode === 'ready') return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySetupWpSync = (nextUi = {}) => {
    const nextState = {
      ...state,
      ui: { ...(state.ui || {}), ...nextUi },
      runway: state.runway,
      accounts: state.accounts,
      workPackages: state.workPackages,
    };
    applySetupWorkPackageSync(nextState, d, { today, patch });
  };

  const confirmPriority = () => {
    const chosen = recommended.filter((r) => selected.has(r.id));
    for (const r of chosen) {
      patch('accounts', r.id, { tier: 'Tier A', tierReason: r.reason || 'Founder-confirmed priority account' });
    }
    for (const r of recommended) {
      if (selected.has(r.id) || !r.already) continue;
      patch('accounts', r.id, { tier: 'Qualified Target', tierReason: 'Removed from priority list during confirmation' });
    }
    set('ui.founderQueueConfirmedAt', today);
    set('ui.founderQueueDeferred', false);
    add('decisions', {
      id: uid('dec'),
      type: 'Founder queue',
      question: `Confirm ${chosen.length} priority account${chosen.length === 1 ? '' : 's'} for Week-1 / founder queue?`,
      title: `Confirmed ${chosen.length} priority accounts`,
      date: today,
      options: chosen.map((c) => c.name).filter(Boolean).join(', '),
      evidence: 'Finish setup confirmation',
      decision: `Confirmed ${chosen.length} priority accounts: ${chosen.map((c) => c.name).filter(Boolean).join(', ')}`,
      rationale: chosen.map((c) => c.name).filter(Boolean).join(', '),
    });
    applySetupWpSync({ founderQueueConfirmedAt: today, founderQueueDeferred: false });
    setMsg(`Confirmed ${chosen.length} priority account${chosen.length === 1 ? '' : 's'}.`);
  };

  const deferQueue = () => {
    set('ui.founderQueueDeferred', true);
    applySetupWpSync({ founderQueueDeferred: true });
    setMsg('Priority list deferred — you can confirm later from Pipeline.');
  };

  const proceedUnknown = () => {
    set('ui.runwayUnknownProceed', true);
    applySetupWpSync({ runwayUnknownProceed: true });
    setMsg('Runway Unknown — proceeding. Baseline remains incomplete.');
  };

  const cur = state.settings?.currency;
  const stillUnknown = [];
  if (!operating.queueOk) stillUnknown.push('Priority account list not confirmed');
  if (!operating.runwayOk) stillUnknown.push('Runway cash and burn not recorded');

  const renderRow = (r) => (
    <label key={r.id} className="row" style={{ padding: '8px 12px', borderBottom: '1px solid var(--line-2)', gap: 10, cursor: 'pointer' }}>
      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
      <span style={{ flex: 1 }}>
        <strong>{r.name || 'Unnamed'}</strong>
        <span className="dim tiny" style={{ display: 'block' }}>
          {r.reason || 'Why selected: fit signal from imported research'}
        </span>
      </span>
    </label>
  );

  return (
    <section className="section">
      <Card
        title="Finish setup"
        sub="Confirm your priority accounts so Bridge can plan Today. Soft gate until confirmed — Today stays in Plan updating… until you confirm or defer."
      >
        <p className="bridge-copy">{operating.inferSummary}</p>
        {stillUnknown.length > 0 && (
          <Banner tone="warn">Still unknown: {stillUnknown.join(' · ')}</Banner>
        )}

        {accounts.length === 0 ? (
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <button type="button" className="btn primary" onClick={() => go('pipeline')}>Import / sync accounts</button>
            <button type="button" className="btn ghost" onClick={() => go('settings')}>Settings / CRM</button>
          </div>
        ) : (
          <>
            <div className="hr" />
            <div className="eyebrow">Recommended priority accounts</div>
            <div className="row wrap" style={{ gap: 8, margin: '8px 0' }}>
              <button type="button" className="btn sm" onClick={() => setSelected(new Set(recommended.map((r) => r.id)))}>Select all</button>
              <button type="button" className="btn sm ghost" onClick={() => setSelected(new Set())}>Clear</button>
              <span className="dim tiny">{selected.size} selected · target ~10–15</span>
            </div>
            <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid var(--line-2)', borderRadius: 8 }}>
              {recommendedRows.map(renderRow)}
            </div>

            {alternateRows.length > 0 && (
              <>
                <div className="eyebrow" style={{ marginTop: 12 }}>Alternates</div>
                <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid var(--line-2)', borderRadius: 8, marginTop: 6 }}>
                  {alternateRows.map(renderRow)}
                </div>
              </>
            )}

            <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
              <button type="button" className="btn sm primary" onClick={confirmPriority} disabled={!selected.size}>
                Confirm {selected.size} priority account{selected.size === 1 ? '' : 's'}
              </button>
              <button type="button" className="btn sm ghost" onClick={deferQueue}>Defer for now</button>
            </div>

            <div className="hr" />
            <div className="eyebrow">Time allocation (display only)</div>
            <div className="row wrap" style={{ gap: 12, marginTop: 6 }}>
              {timeTargets.length
                ? timeTargets.map((t) => <Chip key={t.key}>{t.label} {t.target}%</Chip>)
                : <span className="dim tiny">Set an organization plan to lock time allocation.</span>}
            </div>

            <div className="hr" />
            <div className="eyebrow">Runway</div>
            {!operating.runwayOk ? (
              <>
                <div className="grid g2" style={{ gap: 12, marginTop: 8, maxWidth: 520 }}>
                  <Money label="Cash on hand" currency={cur} value={state.runway.cashToday} onChange={(v) => set('runway.cashToday', v)} term="cashOnHand" />
                  <Money label="Current monthly burn" currency={cur} value={state.runway.monthlyBurn} onChange={(v) => set('runway.monthlyBurn', v)} term="currentMonthlyBurn" />
                </div>
                <button type="button" className="btn sm ghost" style={{ marginTop: 10 }} onClick={proceedUnknown}>
                  Runway Unknown — proceed
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  style={{ marginTop: 10, marginLeft: 8 }}
                  onClick={() => {
                    set('ui.runwayConsultingLean', true);
                    set('ui.runwayUnknownProceed', true);
                    applySetupWpSync({ runwayUnknownProceed: true });
                    setMsg('Consulting lean / N/A runway — proceeding without inventing burn.');
                  }}
                >
                  Consulting lean / N/A
                </button>
              </>
            ) : (
              <p className="dim tiny" style={{ marginTop: 6 }}>
                {state.ui?.runwayUnknownProceed
                  ? 'Proceeding with runway Unknown — baseline not established.'
                  : 'Runway baseline recorded.'}
              </p>
            )}

            {msg ? <p className="dim tiny" style={{ marginTop: 8 }}>{msg}</p> : null}
          </>
        )}
      </Card>
    </section>
  );
}
